import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../services/supabase';
import { storage } from '../utils/storage';
import { Platform } from '../utils/platform';

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface SavingGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  target_date: string | null;
  icon: string | null;
  color: string | null;
  notes: string | null;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

interface SavingGoalsState {
  goals: SavingGoal[];
  isLoading: boolean;
  error: string | null;
}

export interface SavingGoalsStore extends SavingGoalsState {
  fetchGoals: (userId: string) => Promise<SavingGoal[]>;
  addGoal: (goal: Omit<SavingGoal, 'id' | 'created_at' | 'updated_at'>) => Promise<SavingGoal>;
  updateGoal: (goalId: string, updates: Partial<SavingGoal>) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  contributeToGoal: (goalId: string, amount: number) => Promise<void>;
  withdrawFromGoal: (goalId: string, amount: number) => Promise<void>;
  markGoalAsCompleted: (goalId: string) => Promise<void>;
  assignTransactionToGoal: (payload: { goalId: string; transactionId: string }) => Promise<void>;
  unassignTransactionFromGoal: (payload: { goalId: string; transactionId: string }) => Promise<void>;
  fetchGoalTransactions: (goalId: string) => Promise<any[]>;
}

export const useSavingGoalsStore = create<SavingGoalsStore>()(
  persist(
    (set, get) => ({
      goals: [],
      isLoading: false,
      error: null,

      fetchGoals: async (userId: string) => {
        try {
          set({ isLoading: true, error: null });

          const { data, error } = await supabase
            .from('saving_goals')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;

          set({ goals: data || [], isLoading: false });
          return data || [];
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch saving goals';
          set({ error: errorMessage, isLoading: false });
          console.error('Error fetching saving goals:', error);
          return [];
        }
      },

      addGoal: async (goal) => {
        try {
          set({ isLoading: true, error: null });

          const newGoal = {
            ...goal,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { data, error } = await supabase
            .from('saving_goals')
            .insert([newGoal])
            .select()
            .single();

          if (error) throw error;

          set(state => ({
            goals: [...state.goals, data],
            isLoading: false
          }));

          return data;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to add saving goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error adding saving goal:', error);
          throw error;
        }
      },

      updateGoal: async (goalId, updates) => {
        try {
          set({ isLoading: true, error: null });

          const { error } = await supabase
            .from('saving_goals')
            .update({
              ...updates,
              updated_at: new Date().toISOString()
            })
            .eq('id', goalId);

          if (error) throw error;

          set(state => ({
            goals: state.goals.map(goal => 
              goal.id === goalId ? { ...goal, ...updates, updated_at: new Date().toISOString() } : goal
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update saving goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error updating saving goal:', error);
          throw error;
        }
      },

      deleteGoal: async (goalId) => {
        try {
          set({ isLoading: true, error: null });

          const { error } = await supabase
            .from('saving_goals')
            .delete()
            .eq('id', goalId);

          if (error) throw error;

          set(state => ({
            goals: state.goals.filter(goal => goal.id !== goalId),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete saving goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error deleting saving goal:', error);
          throw error;
        }
      },

      contributeToGoal: async (goalId, amount) => {
        try {
          set({ isLoading: true, error: null });
          
          // Get the current goal
          const goal = get().goals.find(g => g.id === goalId);
          if (!goal) throw new Error('Saving goal not found');
          
          const newAmount = goal.current_amount + amount;
          const isCompleted = newAmount >= goal.target_amount;
          
          // Update the goal with the new amount
          const { error } = await supabase
            .from('saving_goals')
            .update({
              current_amount: newAmount,
              is_completed: isCompleted,
              updated_at: new Date().toISOString()
            })
            .eq('id', goalId);

          if (error) throw error;

          set(state => ({
            goals: state.goals.map(g => 
              g.id === goalId 
                ? { 
                    ...g, 
                    current_amount: newAmount, 
                    is_completed: isCompleted,
                    updated_at: new Date().toISOString() 
                  } 
                : g
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to contribute to saving goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error contributing to saving goal:', error);
          throw error;
        }
      },

      withdrawFromGoal: async (goalId, amount) => {
        try {
          set({ isLoading: true, error: null });
          
          // Get the current goal
          const goal = get().goals.find(g => g.id === goalId);
          if (!goal) throw new Error('Saving goal not found');
          
          if (goal.current_amount < amount) {
            throw new Error('Insufficient funds in saving goal');
          }
          
          const newAmount = goal.current_amount - amount;
          
          // Update the goal with the new amount
          const { error } = await supabase
            .from('saving_goals')
            .update({
              current_amount: newAmount,
              is_completed: false, // If withdrawing, goal is no longer completed
              updated_at: new Date().toISOString()
            })
            .eq('id', goalId);

          if (error) throw error;

          set(state => ({
            goals: state.goals.map(g => 
              g.id === goalId 
                ? { 
                    ...g, 
                    current_amount: newAmount, 
                    is_completed: false,
                    updated_at: new Date().toISOString() 
                  } 
                : g
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to withdraw from saving goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error withdrawing from saving goal:', error);
          throw error;
        }
      },

      markGoalAsCompleted: async (goalId) => {
        try {
          set({ isLoading: true, error: null });
          
          // Update the goal as completed
          const { error } = await supabase
            .from('saving_goals')
            .update({
              is_completed: true,
              updated_at: new Date().toISOString()
            })
            .eq('id', goalId);

          if (error) throw error;

          set(state => ({
            goals: state.goals.map(g => 
              g.id === goalId 
                ? { 
                    ...g, 
                    is_completed: true,
                    updated_at: new Date().toISOString() 
                  } 
                : g
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to mark saving goal as completed';
          set({ error: errorMessage, isLoading: false });
          console.error('Error marking saving goal as completed:', error);
          throw error;
        }
      },

      assignTransactionToGoal: async (payload) => {
        try {
          set({ isLoading: true, error: null });

          const { data: transaction, error: fetchError } = await supabase
            .from('transactions')
            .select('amount, currency')
            .eq('id', payload.transactionId)
            .single();

          if (fetchError || !transaction) {
            throw fetchError || new Error('Transaction not found');
          }

          // Insert record in goal_transactions
          const { error: linkError } = await supabase
            .from('goal_transactions')
            .insert({
              goal_id: payload.goalId,
              transaction_id: payload.transactionId
            });

          if (linkError) throw linkError;

          // Get the current goal to update its amount
          const { data: goal, error: goalError } = await supabase
            .from('saving_goals')
            .select('current_amount')
            .eq('id', payload.goalId)
            .single();
            
          if (goalError || !goal) {
            throw goalError || new Error('Goal not found');
          }
          
          // Update goal's current_amount
          const newAmount = goal.current_amount + transaction.amount;
          const { error: updateError } = await supabase
            .from('saving_goals')
            .update({
              current_amount: newAmount,
              updated_at: new Date().toISOString()
            })
            .eq('id', payload.goalId);

          if (updateError) throw updateError;

          // Update local state
          set((state) => ({
            goals: state.goals.map((g) =>
              g.id === payload.goalId
                ? { ...g, current_amount: g.current_amount + transaction.amount, updated_at: new Date().toISOString() }
                : g
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to assign transaction to goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error assigning transaction:', error);
          throw error;
        }
      },

      unassignTransactionFromGoal: async (payload) => {
        try {
          set({ isLoading: true, error: null });

          const { data: transaction, error: fetchError } = await supabase
            .from('transactions')
            .select('amount, currency')
            .eq('id', payload.transactionId)
            .single();

          if (fetchError || !transaction) {
            throw fetchError || new Error('Transaction not found');
          }

          // Remove record from goal_transactions
          const { error: unlinkError } = await supabase
            .from('goal_transactions')
            .delete()
            .match({
              goal_id: payload.goalId,
              transaction_id: payload.transactionId
            });

          if (unlinkError) throw unlinkError;

          // Get the current goal to update its amount
          const { data: goal, error: goalError } = await supabase
            .from('saving_goals')
            .select('current_amount')
            .eq('id', payload.goalId)
            .single();
            
          if (goalError || !goal) {
            throw goalError || new Error('Goal not found');
          }
          
          // Update goal's current_amount
          const newAmount = goal.current_amount - transaction.amount;
          const { error: updateError } = await supabase
            .from('saving_goals')
            .update({
              current_amount: newAmount,
              updated_at: new Date().toISOString()
            })
            .eq('id', payload.goalId);

          if (updateError) throw updateError;

          // Update local state
          set((state) => ({
            goals: state.goals.map((g) =>
              g.id === payload.goalId
                ? { ...g, current_amount: g.current_amount - transaction.amount, updated_at: new Date().toISOString() }
                : g
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to unassign transaction from goal';
          set({ error: errorMessage, isLoading: false });
          console.error('Error unassigning transaction:', error);
          throw error;
        }
      },

      fetchGoalTransactions: async (goalId) => {
        try {
          set({ isLoading: true, error: null });

          const { data, error } = await supabase
            .from('goal_transactions')
            .select(`
              *,
              transaction:transactions(*)
            `)
            .eq('goal_id', goalId);

          if (error) throw error;
          
          set({ isLoading: false });
          return data || [];
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch goal transactions';
          set({ error: errorMessage, isLoading: false });
          console.error('Error fetching goal transactions:', error);
          throw error;
        }
      }
    }),
    {
      name: 'saving-goals-storage',
      storage: createJSONStorage(() => getStorage()),
    },
  ),
); 